import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { UserService } from '@app/services/user/user.service';
import { LocalizationService } from '../internationalization/localization.service';
import { SignInSignOutDocument } from '@app/models/user.model';
import { SiteService } from '@app/services/site/site.service';
import { ReferenceService } from '@app/services/reference/reference.service';
import { VisitorTypeUploadReason } from '@app/models/visitortypeuploadreason.model';
import { SiteVisitReason } from '@app/models/sitevisitreason.model';
import { FileStorageService } from '@app/services/filestorage/filestorage.service';
import { map, Observable } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ConfigurationService } from '@app/services/configuration/configuration.service';

@Component({
  selector: 'app-site-induction-completed-photo-list',
  templateUrl: './site-induction-completed-photo-list.component.html',
  styleUrl: './site-induction-completed-photo-list.component.scss',
  standalone: false
})
export class SiteInductionCompletedPhotoListComponent {

  signInSignOutdocuments : SignInSignOutDocument[] = [];
  visitorTypeUploadReasons : VisitorTypeUploadReason[] = [];
  siteVisitReasons: SiteVisitReason[] = [];
  cols$: Observable<number>;
  constructor(
    public _router: Router,
    private _navigationService: NavigationService,
    private _userService: UserService,
    private _localizationService: LocalizationService,
    private _siteService: SiteService,
    private _referenceService: ReferenceService,
    private _fileStorageService: FileStorageService,
    private bp: BreakpointObserver,
    private _configurationService: ConfigurationService
  ) {
    this._navigationService.onChange(false, false);
    
    this.cols$ = this.bp.observe([
      Breakpoints.XSmall,  // phones
      Breakpoints.Small,   // small tablets
      Breakpoints.Medium,  // tablets
      Breakpoints.Large,   // desktops
      Breakpoints.XLarge   // large desktops
    ]).pipe(
      map(state => {
        if (state.breakpoints[Breakpoints.XSmall]) return 2; // ≤599px
        if (state.breakpoints[Breakpoints.Small]) return 3; // 600–959px
        if (state.breakpoints[Breakpoints.Medium]) return 3; // 960–1279px
        if (state.breakpoints[Breakpoints.Large]) return 4; // 1280–1919px
        return 5; // ≥1920px
      })
    );
  }

  ngOnInit(): void {
    this._userService.getSignIn().then(signInInfo => {
      if (signInInfo !== undefined && signInInfo.signInSignOutId !== undefined && signInInfo.signInSignOutId !== "") {
        this._userService.getSignInSignOutDocuments(signInInfo.signInSignOutId).subscribe({
          next: async (documents) => {
            if (documents && documents.length > 0) {
              this.signInSignOutdocuments = documents;
              await this.getUploadReasons();
              await this.getPhotoList();
            }
          }, error: (error) => {
            if (error.error && error.error.errorCode) {
              alert(this._localizationService.translate(error.error.errorCode));
            }
            else {
              alert(this._localizationService.translate("siteinductioncompleted_fetching_photo_error"));
            }
            console.error(this._localizationService.translate('siteinductioncompleted_fetching_photo_error', error));
          }
        });
      }
      else {
        alert(this._localizationService.translate('induction_completed_add_photo_no_signin_found'));
        this._router.navigate(['signin']);
      }
    });
  }

  async getUploadReasons() {
    this.siteVisitReasons = await this._siteService.getVisitReasons();
    let visitorTypeId = "00000000-0000-0000-0000-000000000000";
    if (this.siteVisitReasons && this.siteVisitReasons.length > 0 &&
        this.siteVisitReasons[0].visitorTypeId && this.siteVisitReasons[0].visitorTypeId != "") {
      visitorTypeId = this.siteVisitReasons[0].visitorTypeId;
    }
    this._referenceService.getUploadReasons(visitorTypeId, true).subscribe({
      next: async (uploadReasons) => {
        if (uploadReasons && uploadReasons.length > 0) {
          this.visitorTypeUploadReasons = uploadReasons;
          this.signInSignOutdocuments.forEach(doc => {
            const reason = this.visitorTypeUploadReasons.find(r => r.id === doc.visitorTypeUploadReasonId);
            doc.visitorTypeUploadReasonDescription = reason ? reason.description : '';
          });
        }
      },
      error: error => {
        if (error.error && error.error.errorCode) {
          alert(this._localizationService.translate(error.error.errorCode));
        }
      }
    })
  }

  getPhotoList() {
    if (this.signInSignOutdocuments && this.signInSignOutdocuments.length > 0) {
      let ids = this.signInSignOutdocuments.map(doc => doc.fileStorageId);
      this._fileStorageService.DownloadFilesByIds(ids).subscribe({
          next: (uploadFileStorages) => {
            this.signInSignOutdocuments.forEach(doc => {
              const uploadFileStorage = uploadFileStorages.find(u => u.id === doc.fileStorageId);
              if (uploadFileStorage){
                doc.fileDataInBase64 = `data:image/${uploadFileStorage.fileType};base64,${uploadFileStorage.file}`;
              }
            });
          },
          error: (error) => {
            console.error(this._localizationService.translate('siteinductioncompleted_fetching_photo_error', error));
          }
      });
      
    }

  }

  closePhotoList() {
    this._router.navigate(['siteinductioncompleted']);
  }

  async addPhotos() {
    if (this.signInSignOutdocuments) {
      let maxPhotos = await this._configurationService.getMaxSignInSignOutPhotos();
      if (this.signInSignOutdocuments.length === maxPhotos) {
        alert(this._localizationService.translate("induction_completed_photo_list_addphotos_max_photos_limit_reached").replace("{count}", `${maxPhotos}`));
      }
      else {
        this._router.navigate(['addnewphoto', true]);
      }
    }
  }

  deletePhoto(doc : SignInSignOutDocument) {
    if (doc && doc.id && doc.id !== "") {
      if (confirm(this._localizationService.translate("induction_completed_photo_list_confirm_delete"))) {
        this._fileStorageService.RemoveDocument(doc.fileStorageId).subscribe({
          next: (_ => {
            this._userService.deleteSignInSignOutDocument(doc.id!).subscribe({
              next: (_ => {
                const indx = this.signInSignOutdocuments
                  .findIndex(v => v.id === doc!.id) ?? -1;
                this.signInSignOutdocuments.splice(indx, indx >= 0 ? 1 : 0);
              })
            });
          })
        });
      }
    }
  }
}
